<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Overlay -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>

    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
        
        <!-- Header -->
        <div class="px-6 py-5 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900">
              Réinitialiser le mot de passe
            </h3>
            <button
              type="button"
              class="text-gray-400 hover:text-gray-600 transition-colors"
              @click="closeModal"
            >
              <XIcon class="h-5 w-5" />
            </button>
          </div>
          <p class="mt-1 text-sm text-gray-500">
            Définir un nouveau mot de passe pour l'utilisateur sélectionné.
          </p>
        </div>

        <!-- Formulaire -->
        <form class="px-6 py-6 space-y-6" @submit.prevent="handleSubmit">
          
          <!-- Informations utilisateur -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-center">
              <UserIcon class="h-5 w-5 text-blue-400 mr-3" />
              <div>
                <p class="text-sm font-medium text-blue-900">{{ user?.name }}</p>
                <p class="text-sm text-blue-700">{{ user?.email }}</p>
                <p class="text-xs text-blue-600 mt-1">Rôle: {{ formatRole(user?.role) }}</p>
              </div>
            </div>
          </div>

          <!-- Nouveau mot de passe -->
          <div>
            <label for="new-password" class="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe <span class="text-red-500">*</span>
            </label>
            <PasswordField
              id="new-password"
              v-model="formData.newPassword"
              name="newPassword"
              placeholder="Choisissez un nouveau mot de passe"
              :required="true"
              :error-message="errors.newPassword"
              :disabled="isSubmitting"
              autocomplete="new-password"
              :left-icon="KeyIcon"
              :toggleable="true"
              :show-strength-indicator="true"
              @blur="validateField('newPassword')"
              @strength-change="handlePasswordStrengthChange"
            />
          </div>

          <!-- Confirmation du nouveau mot de passe -->
          <div>
            <label for="confirm-password" class="block text-sm font-medium text-gray-700 mb-2">
              Confirmer le nouveau mot de passe <span class="text-red-500">*</span>
            </label>
            <PasswordField
              id="confirm-password"
              v-model="formData.confirmPassword"
              name="confirmPassword"
              placeholder="Confirmez le nouveau mot de passe"
              :required="true"
              :error-message="errors.confirmPassword"
              :disabled="isSubmitting"
              autocomplete="new-password"
              :left-icon="KeyIcon"
              :toggleable="true"
              @blur="validateField('confirmPassword')"
            />
          </div>

          <!-- Avertissement de sécurité -->
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex">
              <AlertTriangleIcon class="h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
              <div class="text-sm text-yellow-700">
                <p class="font-medium mb-1">⚠️ Attention :</p>
                <ul class="space-y-1 text-xs">
                  <li>• L'utilisateur devra se reconnecter avec le nouveau mot de passe</li>
                  <li>• Cette action ne peut pas être annulée</li>
                  <li>• Le mot de passe sera immédiatement actif</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Conseils de sécurité -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex">
              <ShieldCheckIcon class="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div class="text-sm text-blue-700">
                <p class="font-medium mb-1">Conseils pour un mot de passe sécurisé :</p>
                <ul class="space-y-1 text-xs">
                  <li>• Au moins 8 caractères</li>
                  <li>• Mélange de lettres majuscules et minuscules</li>
                  <li>• Au moins un chiffre</li>
                  <li>• Au moins un caractère spécial</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Boutons d'action -->
          <div class="flex space-x-3 pt-4">
            <button
              type="button"
              class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              Annuler
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="isSubmitting || !isFormValid"
            >
              <div class="flex items-center justify-center">
                <LoadingSpinner v-if="isSubmitting" size="sm" variant="white" :show-text="false" />
                <KeyIcon v-else class="h-4 w-4 mr-2" />
                <span>{{ isSubmitting ? 'Réinitialisation...' : 'Réinitialiser le mot de passe' }}</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { User } from '@/api/types/core/users';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import PasswordField from '@/components/form/PasswordField.vue';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/stores/auth';
import type { AdminResetPasswordRequest } from '@/types/auth';
import {
    AlertTriangle as AlertTriangleIcon,
    Key as KeyIcon,
    ShieldCheck as ShieldCheckIcon,
    User as UserIcon,
    X as XIcon
} from 'lucide-vue-next';
import { computed, reactive, ref, watch } from 'vue';

export interface AdminResetPasswordModalProps {
  modelValue: boolean;
  user: User | null;
}

export interface AdminResetPasswordModalEmits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'success', user: User): void;
  (e: 'error', error: string): void;
}

const props = defineProps<AdminResetPasswordModalProps>();
const emit = defineEmits<AdminResetPasswordModalEmits>();

// Composables
const authStore = useAuthStore();

// État du formulaire
const formData = reactive({
  newPassword: '',
  confirmPassword: '',
});

const errors = reactive({
  newPassword: '',
  confirmPassword: '',
});

const isSubmitting = ref(false);
const passwordStrength = ref({ score: 0, label: '', percentage: 0 });

// Validation
const validateField = (fieldName: keyof typeof errors): void => {
  const field = fieldName as keyof typeof formData;
  const value = formData[field];

  switch (fieldName) {
    case 'newPassword':
      if (!value || value.trim().length === 0) {
        errors.newPassword = 'Le nouveau mot de passe est requis';
      } else if (value.length < 8) {
        errors.newPassword = 'Le mot de passe doit contenir au moins 8 caractères';
      } else {
        const validation = authService.validatePassword(value);
        if (!validation.isValid) {
          errors.newPassword = validation.errors.join(', ');
        } else {
          errors.newPassword = '';
        }
      }
      break;

    case 'confirmPassword':
      if (!value || value.trim().length === 0) {
        errors.confirmPassword = 'La confirmation du mot de passe est requise';
      } else if (value !== formData.newPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
      } else {
        errors.confirmPassword = '';
      }
      break;
  }
};

const validateForm = (): boolean => {
  validateField('newPassword');
  validateField('confirmPassword');

  return errors.newPassword === '' && errors.confirmPassword === '' &&
         formData.newPassword.trim() !== '' && formData.confirmPassword.trim() !== '';
};

const isFormValid = computed(() => {
  return formData.newPassword.trim() !== '' && formData.confirmPassword.trim() !== '' &&
         errors.newPassword === '' && errors.confirmPassword === '' &&
         formData.newPassword === formData.confirmPassword;
});

// Utilitaires
const formatRole = (role?: string): string => {
  const roleMap: Record<string, string> = {
    'admin': 'Administrateur',
    'user': 'Utilisateur',
    'visiteur': 'Visiteur'
  };
  return roleMap[role || ''] || role || 'Inconnu';
};

// Gestionnaires d'événements
const handlePasswordStrengthChange = (strength: { score: number; label: string; percentage: number }): void => {
  passwordStrength.value = strength;
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm() || !props.user) {
    return;
  }

  isSubmitting.value = true;

  try {
    const resetPasswordData: AdminResetPasswordRequest = {
      user_uuid: props.user.uuid,
      new_password: formData.newPassword,
    };

    await authService.adminResetPassword(resetPasswordData, authStore.token!);
    
    // Réinitialiser le formulaire
    resetForm();
    
    // Fermer le modal et émettre le succès
    emit('update:modelValue', false);
    emit('success', props.user);
    
  } catch (error) {
    console.error('Erreur lors de la réinitialisation du mot de passe:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe';
    emit('error', errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const closeModal = (): void => {
  if (!isSubmitting.value) {
    resetForm();
    emit('update:modelValue', false);
  }
};

const resetForm = (): void => {
  formData.newPassword = '';
  formData.confirmPassword = '';
  
  errors.newPassword = '';
  errors.confirmPassword = '';
  
  passwordStrength.value = { score: 0, label: '', percentage: 0 };
};

// Réinitialiser le formulaire quand le modal s'ouvre
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    resetForm();
  }
});
</script> 